import {useMediaQuery} from '@chakra-ui/react';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, Row, UiButton, UiImage, UiQuestionnaireLayout, UiText} from '~/components/elements';
import {AppCarousel} from '~/components/elements/AppCarousel';
import {UiCard} from '~/components/elements/UiCard';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {QUERY_MOBILE} from '~/constants';
import {EMERGENCY_CONTRACEPTION_KEYS, LAST_PROTECT_INTERCOURSE} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {BrandItem, LastProtectIntercourse, Option} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire6Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);
  const [currentCarousel, setCurrentCarousel] = useState(0);
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);

  const lastUnprotectedIntercourseAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[EMERGENCY_CONTRACEPTION_KEYS.LAST_UNPROTECTED_INTERCOURSE]?.data,
  );

  const selectedIntercourse: LastProtectIntercourse = lastUnprotectedIntercourseAnswer?.selected;

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[EMERGENCY_CONTRACEPTION_KEYS.PREFERRED_BRAND]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === EMERGENCY_CONTRACEPTION_KEYS.PREFERRED_BRAND);

  let medicalOptions: BrandItem[] = [];
  let questionnaireOptions: Option[] = [];

  if (questionnaire?.options?.length) questionnaireOptions = questionnaireOptions.concat(questionnaire?.options);

  if (
    selectedIntercourse === LAST_PROTECT_INTERCOURSE.BT_12_48_HOURS.value ||
    selectedIntercourse === LAST_PROTECT_INTERCOURSE.BT_2_4_DAYS.value
  ) {
    questionnaireOptions = questionnaireOptions.filter(i => i.value === 'Ella');
  }

  questionnaireOptions.map(i => {
    if (i?.data) {
      medicalOptions.push(i.data);
    }
  });

  const {inputs, setInputs} = useInput({
    productId: currentAnswer?.productId || questionnaireOptions[0]?.data?.productId || '',
  });

  const selectedItem = questionnaireOptions.findIndex(i => i.data?.productId === inputs.productId);

  const onClickMedicalButton = (value: string | number, index: number) => {
    setCurrentCarousel(index);
    setInputs({productId: value});
  };

  const onChangeCarousel = (pos: number) => {
    setCurrentCarousel(pos);
    setInputs({productId: questionnaireOptions[pos]?.data?.productId});
  };

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: EMERGENCY_CONTRACEPTION_KEYS.PREFERRED_BRAND,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: EMERGENCY_CONTRACEPTION_KEYS.PURCHASE_PREGNANCY_TEST}));
  };

  const handleGoBack = () => {
    dispatch(
      setQuestionnaireAnswer({
        key: EMERGENCY_CONTRACEPTION_KEYS.PREFERRED_BRAND,
        index: questionnaire?.index || '',
        data: {productId: ''},
      }),
    );
  };

  const renderCarouselItem = (item: BrandItem[]) =>
    item.map(i => (
      <UiCard variant="rounded" boxShadow="md" m={spacing.small} mb={spacing.smedium} px={spacing.small} key={i.name}>
        <UiText fontSize="xl" fontWeight="600">
          {i.name}
        </UiText>
        <UiText fontSize="xl" fontWeight="600">
          {i.unit}
          {i.price}*
        </UiText>
        <UiText fontSize="sm" fontWeight="700">
          {i.ingredient}
        </UiText>
        {i?.content?.map(data => (
          <UiText key={data} fontSize="sm" textAlign="center" mt={spacing.tiny}>
            {data}
          </UiText>
        ))}
        <UiText fontSize="xs" textAlign="center" mt={spacing.small} color="fadedJade">
          {i.description}
        </UiText>
      </UiCard>
    ));

  return (
    <>
      <UiQuestionnaireLayout
        backIcon
        handleOnNext={onClickSubmit}
        handleGoBack={handleGoBack}
        disableNext={!inputs.productId}
        actions={questionnaire?.actions}
        checkoutStep={questionnaire?.checkoutStep}
      >
        <Col alignItems="center" textAlign="center">
          <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
          <UiText fontSize="lg" fontWeight="bold" mt={spacing.large}>
            {questionnaire?.questions?.default}
          </UiText>
          {questionnaire?.descriptions?.default?.map(i => (
            <UiText key={i} textAlign="center" fontSize="sm" mt={spacing.small} mx={spacing.tiny}>
              {i.replace(`{whenDid}`, selectedIntercourse ? LAST_PROTECT_INTERCOURSE[selectedIntercourse].label : '')}
            </UiText>
          ))}
          <Row
            mt={spacing.small}
            overflowX="auto"
            width="100%"
            justifyContent={isDesktop || questionnaireOptions?.length < 3 ? 'center' : 'normal'}
            py={spacing.small}
          >
            {questionnaireOptions?.map((i, index) => (
              <ScrollToBottomButton
                key={i?.value}
                title={i?.label}
                mx={spacing.tiny}
                variant={inputs.productId === i.data?.productId ? 'solid' : 'toggle'}
                onClick={() => onClickMedicalButton(i?.data?.productId, index)}
                _focus={{boxShadow: 'none'}}
              />
            ))}
          </Row>
        </Col>
        <AppCarousel
          selectedItem={selectedItem >= 0 ? selectedItem : 0}
          onChange={onChangeCarousel}
          showIndicators={false}
          showArrows={false}
          showStatus={false}
          showThumbs={false}
        >
          {renderCarouselItem(medicalOptions)}
        </AppCarousel>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire6Screen;
